import { AutoAwesome, Close, ExpandMore, Send } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  Drawer,
  Fab,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { isTokenValid } from "../../features/Auth/slice/authSlice";
import { useQueryAiContextMutation } from "../../hooks/ApiHooks";

/* ============================================================================
 * Floating read-only AI assistant.
 *
 * Every suggestion below maps onto a real target/scope pair in the backend
 * catalog (see AI_QUERY_CATALOG.md in the edge-function repo). Suggestions are
 * grouped by the scope they read, because scope is the thing operators get
 * wrong: on a task page, "this store" means the store behind the task, not the
 * task. Grouping the chips makes that visible instead of leaving the model to
 * guess from the wording.
 *
 * Keep the wording concrete ("last 5 breakdowns at this store") rather than
 * open-ended ("what maintenance risk is visible") — the planner can place the
 * first and has to reject the second.
 * ========================================================================== */
const CONTEXTS = {
  task: {
    label: "Task",
    groups: [
      {
        heading: "This task",
        prompts: [
          "Summarise this task and where it currently stands",
          "What has happened on this task so far?",
          "Who is assigned to this task?",
          "What equipment is on this task?",
        ],
      },
      {
        heading: "This store",
        prompts: [
          "What were the last 5 breakdowns at this store?",
          "How many open tasks does this store have right now?",
          "Show this store's tasks broken down by type",
          "Which employee handles the most tasks at this store?",
          "Tell me about this store",
        ],
      },
      {
        heading: "This equipment",
        prompts: [
          "Show the full task history for this equipment",
          "Which parts keep getting replaced on this equipment?",
          "How many breakdowns has this equipment had?",
        ],
      },
    ],
  },
  employee: {
    label: "Employee",
    groups: [
      {
        heading: "Workload",
        prompts: [
          "What is this employee's current workload?",
          "Which of their tasks are still open?",
          "Show their assigned tasks broken down by type",
        ],
      },
      {
        heading: "History",
        prompts: [
          "How many tasks did they close this month?",
          "How many INSTALLATION jobs have they completed?",
          "What were their last 5 breakdown jobs?",
          "Show their monthly task volume for the past year",
          "Which parts do they use most often?",
        ],
      },
    ],
  },
  global: {
    label: "All operations",
    groups: [
      {
        heading: "Right now",
        prompts: [
          "Show the task status distribution across the operation",
          "How many tasks are still open?",
          "How many critical-priority tasks are open?",
          "Which task types are most common?",
        ],
      },
      {
        heading: "Volume and trends",
        prompts: [
          "Which stores have the most tasks?",
          "Which employee closed the most tasks this month?",
          "Which region has the most open tasks?",
          "Show monthly task volume for the past 12 months",
          "What were the last 10 breakdowns company-wide?",
          "How many breakdowns happened this month?",
        ],
      },
    ],
  },
};

/* Only these two pages carry an entity the backend can scope to; both need the
 * id present, since /user/data-analysis with no id shows no employee at all.
 * Everything else — dashboard, task list, inventory, attendance — is global.
 *
 * Note there is intentionally no `store` or `equipment` context: the app has no
 * store or equipment detail page. Store- and equipment-scoped questions are
 * reached from a task page instead, where the backend resolves the store and the
 * equipment from the task itself. */
const CONTEXT_ROUTES = [
  { type: "task", pattern: /^\/task\/detail\/([^/]+)/ },
  { type: "employee", pattern: /^\/user\/data-analysis\/([^/]+)/ },
];

const getPageContext = (pathname) => {
  const match = CONTEXT_ROUTES.find(({ pattern }) => pattern.test(pathname));
  if (!match) return { type: "global", id: null };
  return { type: match.type, id: decodeURIComponent(pathname.match(match.pattern)[1]) };
};

// The scope the backend actually resolved, echoed back so the operator can see
// which store / employee / units the answer was drawn from before trusting it.
const describeScope = (resolved) => {
  if (!resolved) return null;
  if (resolved.store_name) return `Store: ${resolved.store_name}`;
  if (resolved.store_id) return `Store: ${resolved.store_id}`;
  if (resolved.name) return `Employee: ${resolved.name}`;
  if (resolved.equipment_ids?.length) return `Equipment: ${resolved.equipment_ids.join(", ")}`;
  if (resolved.scope) return `Scope: ${resolved.scope}`;
  return null;
};

const CONFIDENCE_COLOUR = { high: "success", medium: "warning", low: "default" };

// Turn the numeric YYYYMMDDHHMMSS date bound the backend applies back into a
// display date. `crt_tms` / `upd_tms` are numeric columns in tsk01, not
// timestamps, so both ends round-trip through this shape.
const formatTmsBound = (value) => {
  if (value === null || value === undefined) return null;
  const s = String(value);
  if (s.length < 8) return null;
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
};

// Render the filters (and group_by dimension) the planner applied as chips so
// the operator can see exactly what shaped the answer. This is the fix for
// the "why did it filter on status when I didn't ask?" class of complaint: an
// inferred filter is now visible, not buried in the model's key_facts.
const AppliedFilters = ({ queryPlan }) => {
  if (!queryPlan) return null;
  const { filters, group_by: groupBy } = queryPlan;
  const chips = [];
  if (groupBy) chips.push({ label: `grouped by: ${groupBy}` });
  if (filters?.task_types?.length) chips.push({ label: `type: ${filters.task_types.join(", ")}` });
  if (filters?.statuses?.length) chips.push({ label: `status: ${filters.statuses.join(", ")}` });
  if (filters?.priorities?.length) chips.push({ label: `priority: ${filters.priorities.join(", ")}` });
  if (filters?.store_name) chips.push({ label: `store: ${filters.store_name}` });
  if (filters?.keyword) chips.push({ label: `keyword: ${filters.keyword}` });
  if (filters?.open_only) chips.push({ label: "open only" });
  const from = formatTmsBound(filters?.date_from);
  const to = formatTmsBound(filters?.date_to);
  if (from || to) chips.push({ label: `${filters?.date_field || "created"}: ${from || "…"} → ${to || "…"}` });
  if (chips.length === 0) return null;
  return (
    <Stack direction="row" gap={0.5} flexWrap="wrap">
      {chips.map(({ label }) => (
        <Chip key={label} size="small" color="info" variant="outlined" label={label} />
      ))}
    </Stack>
  );
};

const AnsweredResult = ({ response }) => {
  const result = response.ai?.claude?.result || response.ai?.mistral?.result;
  if (!result) return <Alert severity="warning" sx={{ mt: 2 }}>The assistant read the data but could not write an answer. Please try again.</Alert>;

  const scope = describeScope(response.scope_resolved_to);
  const rowCount = Array.isArray(response.result) ? response.result.length : 0;

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      <Stack direction="row" gap={0.5} flexWrap="wrap">
        {scope ? <Chip size="small" label={scope} /> : null}
        <Chip size="small" variant="outlined" label={`${rowCount} row${rowCount === 1 ? "" : "s"} read`} />
        {result.confidence ? (
          <Chip size="small" color={CONFIDENCE_COLOUR[result.confidence] || "default"} label={`${result.confidence} confidence`} />
        ) : null}
      </Stack>

      <AppliedFilters queryPlan={response.query_plan} />

      <Typography variant="body2">{result.answer || "No answer returned."}</Typography>

      {result.highlights?.length ? (
        <Stack direction="row" gap={0.75} flexWrap="wrap">
          {result.highlights.map(({ label, value }) => (
            <Chip key={`${label}-${value}`} size="small" variant="outlined" label={`${label}: ${value}`} />
          ))}
        </Stack>
      ) : null}

      {result.key_facts?.length ? (
        <Box>
          <Typography variant="subtitle2">Details</Typography>
          <Stack component="ul" spacing={0.25} sx={{ pl: 2.5, m: 0, mt: 0.5 }}>
            {result.key_facts.map((fact, index) => (
              <Typography component="li" variant="body2" key={`${fact.slice(0, 24)}-${index}`}>{fact}</Typography>
            ))}
          </Stack>
        </Box>
      ) : null}

      {result.referenced_task_ids?.length ? (
        <Box>
          <Typography variant="subtitle2">Tasks referenced</Typography>
          <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
            {result.referenced_task_ids.map((taskId) => <Chip key={taskId} size="small" label={taskId} />)}
          </Stack>
        </Box>
      ) : null}

      {result.caveats ? <Alert severity="info" sx={{ py: 0 }}>{result.caveats}</Alert> : null}

      <Typography variant="caption" color="text.secondary">AI-generated. Verify before acting.</Typography>
    </Stack>
  );
};

const AssistantResult = ({ response }) => {
  if (!response) return null;

  // `needs` names the one missing piece, so the operator can add it and re-ask
  // rather than the assistant guessing at what was meant.
  if (response.status === "cannot_process") {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">{response.reason || "This question cannot be answered from the current page."}</Typography>
        {response.needs ? (
          <Typography variant="body2" sx={{ mt: 0.5 }}>Try adding: {response.needs}</Typography>
        ) : null}
      </Alert>
    );
  }

  if (response.status === "answered") return <AnsweredResult response={response} />;
  return null;
};

export default function AiAssistantBubble() {
  const { pathname } = useLocation();
  const isLoggedIn = useSelector(isTokenValid);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { triggerMutation, isLoading, isError, error, data, reset } = useQueryAiContextMutation();

  const pageContext = useMemo(() => getPageContext(pathname), [pathname]);
  const context = CONTEXTS[pageContext.type];
  const askedFor = data?.data;

  // A stale answer about another task read as if it were about this one, so drop
  // it whenever the page's entity changes. `reset` is deliberately NOT a
  // dependency: RTK Query gives it a new identity on every triggerMutation call
  // (its requestId changes), so depending on it re-fires this effect right after
  // a request goes out and wipes the answer the instant it arrives.
  const resetRef = useRef(reset);
  resetRef.current = reset;
  useEffect(() => {
    setQuery("");
    resetRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageContext.type, pageContext.id]);

  // Chips collapse behind an accordion once the body has an answer / error /
  // in-flight request so the answer is above the fold; they auto-re-expand
  // when the body clears (page change, no result yet). The accordion is still
  // manually toggleable so operators can pick a follow-up question mid-answer.
  const hasBodyContent = !!askedFor || isError || isLoading;
  const [chipsExpanded, setChipsExpanded] = useState(true);
  useEffect(() => {
    setChipsExpanded(!hasBodyContent);
  }, [hasBodyContent]);

  const inputRef = useRef(null);

  const ask = useCallback(
    (value) => {
      const trimmed = String(value ?? query).trim();
      if (trimmed) {
        triggerMutation(pageContext.type, pageContext.id, trimmed);
        setQuery('');
      }
    },
    [query, pageContext.type, pageContext.id, triggerMutation]
  );

  // `multiline` swallows plain Enter as a newline, so give operators a submit
  // shortcut that matches the "chat-style" expectation.
  const onInputKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      ask();
    }
  };

  // Only surface the assistant to authenticated users — the auth pages
  // (login / reset / signup / verify) share this layout and shouldn't show it.
  if (!isLoggedIn) return null;

  return (
    <Fragment>
      <Fab
        color="primary"
        aria-label="Open AI assistant"
        onClick={() => setOpen(true)}
        sx={{ position: "fixed", right: 24, bottom: 24, zIndex: (theme) => theme.zIndex.drawer + 2 }}
      >
        <AutoAwesome />
      </Fab>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        // Sit above the fixed AppBar (drawer+1 = 1201) AND the global
        // API-inflight `TopDeterminantProgress` bar (1302) in DashboardLayout,
        // otherwise that thin progress stripe cuts across the drawer's header
        // area where the context chip sits. Offset the Paper so the LOCAL
        // environment's debug bar at the very top stays visible.
        sx={{ zIndex: (theme) => theme.zIndex.modal + 3 }}
        PaperProps={{ sx: { top: "var(--app-bar-height)", height: "calc(100% - var(--app-bar-height))" } }}
        // Focus after the slide-in finishes; focusing during the transition
        // sometimes lands on the drawer root instead of the input.
        SlideProps={{ onEntered: () => inputRef.current?.focus() }}
      >
        <Stack sx={{ width: { xs: "min(100vw, 420px)", sm: 420 }, height: "100%", p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={1} alignItems="center">
              <AutoAwesome color="primary" />
              <Typography variant="h6">AI Assistant</Typography>
            </Stack>
            <IconButton aria-label="Close AI assistant" onClick={() => setOpen(false)}><Close /></IconButton>
          </Stack>

          <Stack direction="row" gap={0.5} alignItems="center" sx={{ mt: 1 }}>
            <Chip size="small" color="primary" label={`${context.label}${pageContext.id ? `: ${pageContext.id}` : ""}`} />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
            Read-only. Answers come from this page&apos;s data only — nothing is changed.
          </Typography>

          <Box sx={{ flex: 1, overflowY: "auto", mt: 2 }}>
            {isLoading ? <LinearProgress sx={{ mb: 1.5 }} /> : null}
            {isError ? (
              <Alert severity="error" sx={{ mb: 1.5 }}>{error?.data?.message || "Assistant request failed."}</Alert>
            ) : null}
            <AssistantResult response={askedFor} />

            {hasBodyContent ? <Divider sx={{ my: 1.5 }} /> : null}

            <Accordion
              disableGutters
              elevation={0}
              expanded={chipsExpanded}
              onChange={(_, expanded) => setChipsExpanded(expanded)}
              sx={{
                background: "transparent",
                "&:before": { display: "none" },
                "& .MuiAccordionSummary-root": { px: 0, minHeight: 0 },
                "& .MuiAccordionSummary-content": { my: 0.5 },
                "& .MuiAccordionDetails-root": { px: 0, pt: 0.5 },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="overline" color="text.secondary">
                  {hasBodyContent ? "Ask another question" : "Suggested questions"}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {context.groups.map(({ heading, prompts }) => (
                  <Box key={heading} sx={{ mb: 1.5 }}>
                    <Typography variant="overline" color="text.secondary">{heading}</Typography>
                    <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                      {prompts.map((prompt) => (
                        <Chip
                          key={prompt}
                          label={prompt}
                          variant="outlined"
                          disabled={isLoading}
                          sx={{ height: "auto", justifyContent: "flex-start", py: 0.6, "& .MuiChip-label": { whiteSpace: "normal", textAlign: "left" } }}
                          onClick={() => { setQuery(prompt); ask(prompt); }}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          </Box>

          <Stack spacing={1} component="form" sx={{ pt: 1 }} onSubmit={(event) => { event.preventDefault(); ask(); }}>
            <TextField
              inputRef={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onInputKeyDown}
              placeholder="Ask about this data..."
              helperText="Press Ctrl/Cmd + Enter to send"
              multiline
              minRows={2}
              inputProps={{ maxLength: 1000 }}
              disabled={isLoading}
            />
            <LoadingButton loading={isLoading} type="submit" variant="contained" startIcon={<Send />} disabled={!query.trim() || isLoading}>
              Ask AI
            </LoadingButton>
          </Stack>
        </Stack>
      </Drawer>
    </Fragment>
  );
}
